import interfaceBridge from 'utils/interfaceBridge';
import { NativeFeatureChecker } from 'utils/NativeFeatureChecker';

export type ThemeMode = 'light' | 'dark';
export type ThemeSetting = ThemeMode | 'system';

type ThemeListener = (theme: ThemeMode) => void;

class ThemeManager {
  private static readonly DEFAULT_THEME: ThemeMode = 'dark';
  private static readonly DEFAULT_THEME_SETTING: ThemeSetting = 'dark';

  private static readonly neutral = {
    100: '#F3F4F6',
    200: '#dee0e3',
    300: '#c2c4c7',
    400: '#a1a3a9',
    500: '#7e8188',
    600: '#5e6168',
    700: '#42444d',
    800: '#2b2d37',
    900: '#1C1F28',
  };

  private static readonly neutralOrder = [
    100, 200, 300, 400, 500, 600, 700, 800, 900,
  ] as const;

  private static readonly listeners = new Set<ThemeListener>();

  private static theme: ThemeMode = ThemeManager.loadThemeFromNative();
  private static themeSetting: ThemeSetting =
    ThemeManager.loadThemeSettingFromNative();

  private static isThemeMode(value: string): value is ThemeMode {
    return value === 'light' || value === 'dark';
  }

  private static isThemeSetting(value: string): value is ThemeSetting {
    return value === 'light' || value === 'dark' || value === 'system';
  }

  private static loadThemeFromNative(): ThemeMode {
    if (!NativeFeatureChecker.isSupported('theme')) {
      return ThemeManager.DEFAULT_THEME;
    }

    try {
      const nativeTheme = interfaceBridge.getTheme().toLowerCase();
      return ThemeManager.isThemeMode(nativeTheme)
        ? nativeTheme
        : ThemeManager.DEFAULT_THEME;
    } catch {
      return ThemeManager.DEFAULT_THEME;
    }
  }

  private static loadThemeSettingFromNative(): ThemeSetting {
    if (!NativeFeatureChecker.isSupported('theme')) {
      return ThemeManager.DEFAULT_THEME_SETTING;
    }

    try {
      const nativeThemeSetting = interfaceBridge.getThemeSetting().toLowerCase();
      return ThemeManager.isThemeSetting(nativeThemeSetting)
        ? nativeThemeSetting
        : 'system';
    } catch {
      return ThemeManager.DEFAULT_THEME_SETTING;
    }
  }

  static getTheme(): ThemeMode {
    return ThemeManager.theme;
  }

  static setTheme(theme: ThemeMode): void {
    ThemeManager.theme = theme;
    ThemeManager.listeners.forEach((listener) => listener(theme));
  }

  static getThemeSetting(): ThemeSetting {
    return ThemeManager.themeSetting;
  }

  static setThemeSetting(themeSetting: ThemeSetting): void {
    if (ThemeManager.themeSetting === themeSetting) {
      return;
    }

    if (NativeFeatureChecker.isSupported('theme')) {
      interfaceBridge.setThemeSetting(themeSetting);
    }

    ThemeManager.themeSetting = themeSetting;
    ThemeManager.refreshThemeFromNative();
  }

  static getNeutral(level: number): string {
    const safeLevel = Number.isFinite(level) ? level : 5;
    const normalizedLevel = Math.min(9, Math.max(1, Math.round(safeLevel)));
    const index = normalizedLevel - 1;
    const neutralIndex =
      ThemeManager.theme === 'dark'
        ? ThemeManager.neutralOrder.length - 1 - index
        : index;
    const key = ThemeManager.neutralOrder[neutralIndex];

    return ThemeManager.neutral[key];
  }

  static refreshThemeFromNative(): ThemeMode {
    const nextTheme = ThemeManager.loadThemeFromNative();
    ThemeManager.setTheme(nextTheme);
    return nextTheme;
  }

  static refreshThemeSettingFromNative(): ThemeSetting {
    const nextThemeSetting = ThemeManager.loadThemeSettingFromNative();
    ThemeManager.themeSetting = nextThemeSetting;
    return nextThemeSetting;
  }

  static subscribe(listener: ThemeListener): () => void {
    ThemeManager.listeners.add(listener);
    return () => ThemeManager.listeners.delete(listener);
  }
}

export default ThemeManager;
